import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Tooltip,
  Typography,
  Alert,
  Box,
} from '@mui/material'
import { Add, Close, Delete, InsertDriveFile, Upload } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { Form, required, useNotify, useTranslate } from 'react-admin'
import { blue } from 'src/assets/colors'
import { EditInputsWrap } from 'src/components/uiElements/CRUD/EditInputsWrap'
import { SelectInput } from 'src/components/uiElements/fields/SelectInput'
import { routeConfigServices } from 'src/services/reservation/route-config/routeConfig.service'
import { workUnitPaymentServices } from 'src/services/payment/workUnit/WorkUnit.service'
import { productTypeServices } from 'src/services/reservation/product-type/product_type.service'

export const RouteConfigImportButton = ({ onImportFinished }) => {
  const translate = useTranslate()
  const notify = useNotify()

  const [openModal, setOpenModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [file, setFile] = useState(null)
  const [fileError, setFileError] = useState(false)
  const [workUnitOptions, setWorkUnitOptions] = useState([])
  const [loadingWorkUnits, setLoadingWorkUnits] = useState(false)
  const [productTypeOptions, setProductTypeOptions] = useState([])
  const [loadingProductTypes, setLoadingProductTypes] = useState(false)
  const [importResult, setImportResult] = useState(null)

  const hasWorkUnits = workUnitOptions.length > 0
  const hasProductTypes = productTypeOptions.length > 0

  const shouldDisableSubmit = useMemo(
    () => loading || loadingWorkUnits || !hasWorkUnits,
    [loading, loadingWorkUnits, hasWorkUnits]
  )

  const resetState = () => {
    setFile(null)
    setFileError(false)
    setImportResult(null)
  }

  const onOpenModal = () => {
    setOpenModal(true)
  }

  const onCloseModal = () => {
    setOpenModal(false)
    resetState()
  }

  const handleSelectFile = (selectedFile) => {
    if (selectedFile && selectedFile.length > 0) {
      const currentFile = selectedFile[0]
      setFile({
        name: currentFile.name,
        file: currentFile,
      })
      setFileError(false)
      setImportResult(null)
    }
  }

  const handleRemoveFile = (event) => {
    event.stopPropagation()
    setFile(null)
  }

  const handleDownloadFile = () => {
    if (!file?.file) return
    const url = URL.createObjectURL(file.file)
    const link = document.createElement('a')
    link.href = url
    link.target = '_blank'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const fetchWorkUnits = useCallback(async () => {
    try {
      setLoadingWorkUnits(true)
      const response = await workUnitPaymentServices.getByAll()
      setWorkUnitOptions(response || [])
    } catch (error) {
      setWorkUnitOptions([])
      notify('ra.message.route_config_work_units_error', { type: 'error' })
    } finally {
      setLoadingWorkUnits(false)
    }
  }, [notify])

  const fetchProductTypes = useCallback(async () => {
    try {
      setLoadingProductTypes(true)
      const response = await productTypeServices.getByParam()
      setProductTypeOptions(response || [])
    } catch (error) {
      setProductTypeOptions([])
      notify('ra.message.route_config_product_types_error', { type: 'error' })
    } finally {
      setLoadingProductTypes(false)
    }
  }, [notify])

  useEffect(() => {
    if (openModal && !hasWorkUnits && !loadingWorkUnits) {
      fetchWorkUnits()
    }
    if (openModal && !hasProductTypes && !loadingProductTypes) {
      fetchProductTypes()
    }
  }, [fetchProductTypes, fetchWorkUnits, hasProductTypes, hasWorkUnits, loadingProductTypes, loadingWorkUnits, openModal])

  const handleImport = async (data) => {
    const { work_unit_token, product_type_id } = data
    if (!file?.file) {
      setFileError(true)
      return
    }

    try {
      setLoading(true)
      setImportResult(null)
      const formData = new FormData()
      formData.append('file', file.file)

      const response = await routeConfigServices.importRoutes(
        {
          work_unit_token,
          product_type_id: product_type_id || undefined,
        },
        formData
      )

      if (response?.errors?.length) {
        setImportResult(response)
        notify('ra.message.route_config_import_partial', {
          type: 'warning',
          messageArgs: {
            inserted: response.inserted ?? 0,
            errors: response.errors.length,
          },
        })
        if ((response?.inserted ?? 0) > 0) {
          onImportFinished?.()
        }
      } else {
        notify('ra.message.route_config_import_success', {
          type: 'success',
          messageArgs: {
            inserted: response?.inserted ?? 0,
          },
        })
        onImportFinished?.()
        onCloseModal()
      }
    } catch (error) {
      notify('ra.message.route_config_import_error', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Button
        onClick={onOpenModal}
        disabled={loading}
        startIcon={loading ? <CircularProgress size={20} /> : <Upload fontSize='small' />}
        size='small'
        sx={{ lineHeight: 1.5 }}
      >
        {translate('ra.action.import')}
      </Button>

      <Dialog open={openModal} onClose={onCloseModal} maxWidth='md' fullWidth>
        <DialogTitle display='flex' justifyContent='space-between' alignItems='center'>
          {translate('ra.title.route_config_import')}
          <IconButton size='small' onClick={onCloseModal}>
            <Close />
          </IconButton>
        </DialogTitle>
        <Form onSubmit={handleImport}>
          <DialogContent>
            <Grid container spacing={3}>
              <EditInputsWrap label={translate('ra.field.work_unit')} className='small' required>
                {loadingWorkUnits ? (
                  <Skeleton variant='rectangular' height={40} />
                ) : (
                  <SelectInput
                    source='work_unit_token'
                    options={workUnitOptions}
                    optionsText={['long_name']}
                    optionValue='token'
                    validate={[required()]}
                    size='small'
                    showSearch
                    limitOptions={false}
                  />
                )}
              </EditInputsWrap>

              <EditInputsWrap label={translate('ra.field.product_type')} className='small'>
                {loadingProductTypes ? (
                  <Skeleton variant='rectangular' height={40} />
                ) : (
                  <SelectInput
                    source='product_type_id'
                    options={productTypeOptions}
                    optionsText={['long_name']}
                    optionValue='id'
                    size='small'
                    showSearch
                    limitOptions={false}
                  />
                )}
              </EditInputsWrap>

              <Grid item xs={12}>
                <Button
                  variant='standard'
                  startIcon={<Add />}
                  style={{ color: blue }}
                  component='label'
                >
                  {translate('ra.action.add_file')}
                  <input
                    type='file'
                    hidden
                    inputProps={{ multiple: false }}
                    accept='.csv'
                    onClick={(event) => {
                      event.target.value = null
                    }}
                    onChange={(event) => handleSelectFile(event.target.files)}
                  />
                </Button>
              </Grid>

              {file && (
                <Grid item xs={12}>
                  <Box className='file' onClick={handleDownloadFile}>
                    <InsertDriveFile color='primary' className='file-icon' />
                    <Tooltip title={file?.name}>
                      <Typography className='name'>{file?.name}</Typography>
                    </Tooltip>
                    <IconButton className='delete-icon' size='small' onClick={handleRemoveFile}>
                      <Delete color='error' />
                    </IconButton>
                  </Box>
                </Grid>
              )}

              {fileError && (
                <Grid item xs={12}>
                  <Typography color='error' variant='caption'>
                    {translate('ra.validation.required')}
                  </Typography>
                </Grid>
              )}

              {importResult && (
                <Grid item xs={12}>
                  <Alert severity={importResult.errors?.length ? 'warning' : 'success'}>
                    {translate('ra.message.route_config_import_result', {
                      inserted: importResult.inserted ?? 0,
                      errors: importResult.errors?.length ?? 0,
                    })}
                  </Alert>
                  {!!importResult.errors?.length && (
                    <List dense sx={{ maxHeight: 200, overflowY: 'auto', mt: 1 }}>
                      {importResult.errors.map((error, index) => (
                        <ListItem key={`${index}-${error}`}>
                          <ListItemText primary={error} />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Grid>
              )}
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button onClick={onCloseModal} variant='outlined'>
              {translate('ra.action.cancel')}
            </Button>
            <LoadingButton
              loading={loading}
              loadingPosition='start'
              startIcon={<Upload />}
              variant='contained'
              type='submit'
              disabled={shouldDisableSubmit}
            >
              {translate('ra.action.import')}
            </LoadingButton>
          </DialogActions>
        </Form>
      </Dialog>
    </>
  )
}
